import {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {getOptionalAddOns} from '@amblique/king-living/app/utils/product-utils';
import {
  buildOptionSlotSelectionFromProduct,
  filterVisibleOptionProducts,
} from '@amblique/king-living/app/utils/customize-selections';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';

import OptionalAddOnGroup from '@amblique/king-living/app/components/product-view/optional-add-ons/optional-add-on-group';

/**
 * Part 1 of the Optional Add-ons section.
 * Reads optional add-on slots (isRequired:false) from the product's `extra` container,
 * renders one OptionalAddOnGroup per slot, and wires selections into the
 * CustomizeJourneyProvider via commitInlineSelections.
 *
 * Selections are stored under the dedicated `optionalAddOns` key in the SelectionsMap —
 * separate from flyout `extra` slots — so they are excluded from the
 * "Customize your own" trigger summary and price total while still being
 * available for add-to-cart.
 *
 * The section is always enabled to allow users to select add-ons at any time.
 *
 * @param {Object} props
 * @param {Object} props.product - Full SCAPI product object
 * @param {Function} [props.onAddOnDetailsOpen] - Handler for opening add-on details (desktop only)
 */
const AdditionalAddOns = ({product, onAddOnDetailsOpen}) => {
  const styles = useMultiStyleConfig('OptionalAddOns', {});
  const {committedSelection, commitInlineSelections} = useCustomizeJourney();

  const isDisabled = false;

  const slotDefinitions = useMemo(
    () => getOptionalAddOns(product).filter((v) => filterVisibleOptionProducts(v?.products).length > 0),
    [product]
  );

  const handleSelectionChange = useCallback((slotIndex, productObj) => {
    const current = committedSelection.selections ?? {};
    const addOnSlots = [...(current.optionalAddOns?.addons || [])];

    if (productObj == null) {
      addOnSlots[slotIndex] = null;
    } else {
      const slotValue = slotDefinitions[slotIndex];

      addOnSlots[slotIndex] = buildOptionSlotSelectionFromProduct(
        productObj,
        slotIndex,
        slotValue ?? {},
        {containerName: 'optionalAddOns'}
      );
    }

    commitInlineSelections({
      ...current,
      optionalAddOns: {
        ...current.optionalAddOns,
        addons: addOnSlots,
      },
    });
  }, [committedSelection.selections, commitInlineSelections, slotDefinitions]);

  if (!slotDefinitions.length) return null;

  const addOnSelections = committedSelection.selections?.optionalAddOns?.addons ?? [];

  return (
    <Box className="additional-add-ons__root" {...styles.body}>
      {slotDefinitions.map((value, slotIndex) => (
        <OptionalAddOnGroup
          key={value.recommendationTypeId ?? slotIndex}
          isDisabled={isDisabled}
          selectedProductId={addOnSelections[slotIndex]?.productId ?? null}
          slotIndex={slotIndex}
          value={value}
          onAddOnDetailsOpen={onAddOnDetailsOpen}
          onSelectionChange={handleSelectionChange}
        />
      ))}
    </Box>
  );
};

AdditionalAddOns.displayName = 'AdditionalAddOns';

AdditionalAddOns.propTypes = {
  product: PropTypes.object.isRequired,
  onAddOnDetailsOpen: PropTypes.func,
};

export default AdditionalAddOns;
